import React from "react";
import { AreaTrend } from "@/features/investors/components/charts/AreaTrend";
import { Bars } from "@/features/investors/components/charts/Bars";
import { Sparkline } from "@/features/investors/components/charts/Sparkline";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";
import {
    CardHeader,
    DeltaChip,
    InsightHtml,
    SectionHead,
} from "@/features/investors/components/atoms/InvestorsAtoms";
import { InvestorsDashboardData } from "@/features/investors/types/investors.types";
import { InvestorsInsights } from "@/features/investors/utils/investors-insights";
import { investorsFormatters as fmt } from "@/features/investors/utils/investors-formatters";

type Props = {
    data: InvestorsDashboardData;
    insights: InvestorsInsights;
};

export const GrowthSection: React.FC<Props> = ({ data, insights }) => {
    const monthly = data.growth.monthly;
    const labels = monthly.map((month) => month.label);

    const smallCards = [
        {
            title: "Gross profit",
            values: monthly.map((month) => month.profit),
            color: CHART_COLORS.green,
            headline: fmt.moneyCompact(monthly.reduce((total, month) => total + month.profit, 0)),
            metric: data.executive.profit,
        },
        {
            title: "Active accounts",
            values: monthly.map((month) => month.activeAccounts),
            color: CHART_COLORS.violet,
            headline:
                data.executive.activeAccounts.value !== null
                    ? fmt.num(data.executive.activeAccounts.value)
                    : "—",
            metric: data.executive.activeAccounts,
        },
        {
            title: "Completed operations",
            values: monthly.map((month) => month.operations),
            color: CHART_COLORS.pink,
            headline: fmt.numShort(monthly.reduce((total, month) => total + month.operations, 0)),
            metric: data.executive.completedOperations,
        },
    ];

    return (
        <section className="sec" id="sec-growth">
            <SectionHead
                index="02"
                title="Growth"
                question={
                    <>
                        Question: <em>How fast are we growing — and is it accelerating?</em>
                    </>
                }
            />
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <div className="card">
                    <CardHeader
                        title="GMV / transaction volume"
                        sub={`Monthly, ${data.period.label} · ${data.executive.gmv.reason ?? ""}`}
                        tag={<DeltaChip metric={data.executive.gmv} />}
                    />
                    <AreaTrend
                        values={monthly.map((month) => month.gmv)}
                        labels={labels}
                        color={CHART_COLORS.blue}
                        formatValue={fmt.moneyCompact}
                    />
                </div>
                <div className="card">
                    <CardHeader
                        title="Revenue"
                        sub={`Monthly, ${data.period.label}`}
                        tag={<DeltaChip metric={data.executive.revenue} />}
                    />
                    <Bars
                        values={monthly.map((month) => month.revenue)}
                        labels={labels}
                        color={CHART_COLORS.teal}
                        formatValue={fmt.moneyCompact}
                    />
                </div>
            </div>
            <div className="grid" style={{ gridTemplateColumns: "repeat(3,1fr)", marginTop: 20 }}>
                {smallCards.map((card) => (
                    <div key={card.title} className="card">
                        <div className="card-h">
                            <div className="ttl" style={{ fontSize: 13 }}>
                                {card.title}
                            </div>
                            <DeltaChip metric={card.metric} />
                        </div>
                        <div
                            style={{
                                fontFamily: "var(--vb-font-display)",
                                fontWeight: 600,
                                fontSize: 22,
                                letterSpacing: "-.02em",
                                marginBottom: 6,
                            }}
                        >
                            {card.headline}
                        </div>
                        <Sparkline values={card.values} color={card.color} width={220} height={46} />
                    </div>
                ))}
            </div>
            <div style={{ marginTop: 20 }}>
                <InsightHtml html={insights.notes.growth} />
            </div>
        </section>
    );
};
